import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { springPresets } from "@/lib/motion";
import { QUIZ_QUESTIONS } from "@/lib/index";
import { CheckCircle, XCircle, RotateCcw, Trophy } from "lucide-react";

export function Quiz() {
  const [currentQ, setCurrentQ] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [answered, setAnswered] = useState(false);

  const question = QUIZ_QUESTIONS[currentQ];

  const handleAnswer = (index: number) => {
    if (answered) return;
    setSelectedAnswer(index);
    setAnswered(true);
    if (index === question.correctIndex) {
      setScore((s) => s + 1);
    }
  };

  const handleNext = () => {
    if (currentQ + 1 < QUIZ_QUESTIONS.length) {
      setCurrentQ((q) => q + 1);
      setSelectedAnswer(null);
      setAnswered(false);
    } else {
      setShowResult(true);
    }
  };

  const handleRestart = () => {
    setCurrentQ(0);
    setSelectedAnswer(null);
    setAnswered(false);
    setScore(0);
    setShowResult(false);
  };

  const getScoreMsg = () => {
    const pct = (score / QUIZ_QUESTIONS.length) * 100;
    if (pct === 100) return { msg: "Mükemmel! 23 Nisan uzmanısın! 🏆", color: "text-green-600" };
    if (pct >= 80) return { msg: "Harika! Çok şey biliyorsun! 🌟", color: "text-blue-600" };
    if (pct >= 60) return { msg: "İyi iş! Biraz daha öğrenebilirsin! 📚", color: "text-yellow-600" };
    return { msg: "Gazeteyi tekrar oku, öğrenmeye devam et! 💪", color: "text-primary" };
  };

  return (
    <section id="bilgi-yarismasi" className="py-12 px-4 bg-secondary/30">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-black text-primary mb-2" style={{ fontFamily: "'Merriweather', serif" }}>
            🧠 Bilgi Yarışması
          </h2>
          <p className="text-muted-foreground">Ne kadar biliyorsun? Test et!</p>
          <div className="w-24 h-1 bg-primary mx-auto mt-3 rounded-full" />
        </div>

        <AnimatePresence mode="wait">
          {!showResult ? (
            <motion.div
              key={currentQ}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={springPresets.gentle}
              className="bg-card border-2 border-border rounded-2xl p-6 sm:p-8"
              style={{ boxShadow: "0 8px 32px -8px oklch(0.48 0.24 12 / 0.15)" }}
            >
              {/* Progress */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm text-muted-foreground font-medium">
                  Soru {currentQ + 1} / {QUIZ_QUESTIONS.length}
                </span>
                <div className="flex gap-1.5">
                  {QUIZ_QUESTIONS.map((_, i) => (
                    <div
                      key={i}
                      className={`w-6 h-1.5 rounded-full transition-colors ${
                        i < currentQ ? "bg-primary" : i === currentQ ? "bg-primary/60" : "bg-border"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm font-bold text-primary">⭐ {score} puan</span>
              </div>

              {/* Question */}
              <h3 className="text-xl font-bold text-foreground mb-6 leading-relaxed">
                {question.question}
              </h3>

              {/* Options */}
              <div className="space-y-3">
                {question.options.map((opt, idx) => {
                  let btnClass = "w-full text-left px-5 py-3.5 rounded-xl border-2 font-medium transition-all text-sm ";
                  if (!answered) {
                    btnClass += "border-border bg-background hover:border-primary hover:bg-primary/5 cursor-pointer";
                  } else if (idx === question.correctIndex) {
                    btnClass += "border-green-500 bg-green-50 text-green-800 cursor-default";
                  } else if (idx === selectedAnswer && idx !== question.correctIndex) {
                    btnClass += "border-destructive bg-red-50 text-destructive cursor-default";
                  } else {
                    btnClass += "border-border bg-background opacity-50 cursor-default";
                  }

                  return (
                    <motion.button
                      key={idx}
                      whileHover={!answered ? { scale: 1.01 } : {}}
                      whileTap={!answered ? { scale: 0.99 } : {}}
                      transition={springPresets.snappy}
                      onClick={() => handleAnswer(idx)}
                      className={btnClass}
                    >
                      <span className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full border-2 border-current flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {String.fromCharCode(65 + idx)}
                        </span>
                        {opt}
                        {answered && idx === question.correctIndex && (
                          <CheckCircle className="w-4 h-4 text-green-600 ml-auto flex-shrink-0" />
                        )}
                        {answered && idx === selectedAnswer && idx !== question.correctIndex && (
                          <XCircle className="w-4 h-4 text-destructive ml-auto flex-shrink-0" />
                        )}
                      </span>
                    </motion.button>
                  );
                })}
              </div>

              {/* Explanation */}
              <AnimatePresence>
                {answered && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={springPresets.gentle}
                    className={`mt-4 p-4 rounded-xl text-sm font-medium ${
                      selectedAnswer === question.correctIndex
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : "bg-red-50 border border-red-200 text-red-800"
                    }`}
                  >
                    <span className="font-bold mr-1">
                      {selectedAnswer === question.correctIndex ? "✅ Doğru!" : "❌ Yanlış!"}
                    </span>
                    {question.explanation}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Next button */}
              {answered && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={springPresets.gentle}
                  className="mt-5 flex justify-end"
                >
                  <button
                    onClick={handleNext}
                    className="bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity"
                  >
                    {currentQ + 1 < QUIZ_QUESTIONS.length ? "Sonraki Soru →" : "Sonuçları Gör 🎉"}
                  </button>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={springPresets.bouncy}
              className="bg-card border-2 border-primary rounded-2xl p-8 text-center"
              style={{ boxShadow: "0 8px 32px -8px oklch(0.48 0.24 12 / 0.2)" }}
            >
              <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-2xl font-black text-foreground mb-2">
                {score} / {QUIZ_QUESTIONS.length} Doğru!
              </h3>
              <p className={`text-lg font-semibold mb-6 ${getScoreMsg().color}`}>
                {getScoreMsg().msg}
              </p>
              {/* Stars */}
              <div className="flex justify-center gap-2 mb-6">
                {Array.from({ length: QUIZ_QUESTIONS.length }).map((_, i) => (
                  <span key={i} className={`text-2xl ${i < score ? "text-yellow-400" : "text-gray-300"}`}>
                    ⭐
                  </span>
                ))}
              </div>
              <button
                onClick={handleRestart}
                className="flex items-center gap-2 mx-auto bg-primary text-primary-foreground px-6 py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity"
              >
                <RotateCcw className="w-4 h-4" />
                Tekrar Dene
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
